/**
 * Every combat event emission funnels through here. One pooled payload per event type,
 * allocated once, reset before each fill (ARCHITECTURE.md §6.1) — so a stale field from a
 * previous emit can never leak into a listener, and the hot path never allocates.
 *
 * Listeners must not retain payloads. Copy what you need.
 */

import { createPayloadBank, resetPayload } from '../core/index.js';

const EMITTED = [
  'combat:move-start', 'combat:active', 'combat:whiff', 'combat:hit', 'combat:block',
  'combat:counter', 'combat:parry', 'combat:armor', 'combat:throw', 'combat:throw-break',
  'combat:launch', 'combat:wall-splat', 'combat:wall-bounce', 'combat:knockdown',
  'combat:wakeup', 'combat:combo-end', 'combat:finisher', 'combat:round-start',
  'combat:round-end', 'combat:match-end',
  'fighter:state', 'fighter:damage', 'fighter:defeat',
  'audio:cue', 'fx:impact', 'camera:impulse', 'camera:cinematic',
];

export class CombatEmitter {
  constructor(ctx) {
    this.ctx = ctx;
    this.bank = createPayloadBank(EMITTED);
  }

  /** Resets and returns the pooled payload for `name`, ready to fill. */
  begin(name) {
    const p = this.bank[name];
    resetPayload(name, p);
    p.tick = this.ctx.time.tick;
    return p;
  }

  /** Emits the payload previously obtained from `begin`. */
  send(name) {
    this.ctx.events.emit(name, this.bank[name]);
  }

  // -----------------------------------------------------------------------
  // Convenience emitters for the events with fixed shapes
  // -----------------------------------------------------------------------

  state(fighter, from, to, moveId) {
    const p = this.begin('fighter:state');
    p.fighter = fighter;
    p.from = from;
    p.to = to;
    p.moveId = moveId || '';
    this.send('fighter:state');
  }

  damage(fighter, amount, health, maxHealth, source, fatal) {
    const p = this.begin('fighter:damage');
    p.fighter = fighter;
    p.amount = amount;
    p.health = health;
    p.maxHealth = maxHealth;
    p.source = source;
    p.fatal = fatal;
    this.send('fighter:damage');
  }

  defeat(fighter, cause) {
    const p = this.begin('fighter:defeat');
    p.fighter = fighter;
    p.cause = cause;
    this.send('fighter:defeat');
  }

  cue(name, x, y, z, gain = 1, pitch = 1, variant = 0, surface = '') {
    const p = this.begin('audio:cue');
    p.cue = name;
    p.x = x;
    p.y = y;
    p.z = z;
    p.gain = gain;
    p.pitch = pitch;
    p.variant = variant;
    p.surface = surface;
    this.send('audio:cue');
  }

  impact(kind, x, y, z, nx, ny, nz, strength, surface = '', scale = 1, hue = 0) {
    const p = this.begin('fx:impact');
    p.kind = kind;
    p.x = x;
    p.y = y;
    p.z = z;
    p.nx = nx;
    p.ny = ny;
    p.nz = nz;
    p.strength = strength;
    p.surface = surface;
    p.scale = scale;
    p.hue = hue;
    this.send('fx:impact');
  }

  /**
   * Camera impulses always carry a reason (`kind`) and a direction, because
   * ARCHITECTURE.md §7 forbids ambient camera shake — every impulse must be justified.
   */
  cameraImpulse(kind, strength, dirX, dirY, dirZ, durationTicks, shakeHz = 0, zoom = 0) {
    const p = this.begin('camera:impulse');
    p.kind = kind;
    p.strength = strength;
    p.dirX = dirX;
    p.dirY = dirY;
    p.dirZ = dirZ;
    p.durationTicks = durationTicks;
    p.shakeHz = shakeHz;
    p.zoom = zoom;
    this.send('camera:impulse');
  }

  cinematic(shot, subject, target, durationTicks, phase) {
    const p = this.begin('camera:cinematic');
    p.shot = shot;
    p.subject = subject;
    p.target = target;
    p.durationTicks = durationTicks;
    p.phase = phase;
    this.send('camera:cinematic');
  }

  moveStart(fighter, move) {
    const p = this.begin('combat:move-start');
    p.fighter = fighter;
    p.moveId = move.id;
    p.moveName = move.name;
    p.startup = move.startup;
    p.active = move.active;
    p.recovery = move.recovery;
    p.cancelWindowStart = move.cancelWindow[0];
    p.level = move.level;
    p.isThrow = move.isThrow;
    p.isSpecial = move.isSpecial;
    this.send('combat:move-start');
  }

  whiff(fighter, moveId, recoveryFrames) {
    const p = this.begin('combat:whiff');
    p.fighter = fighter;
    p.moveId = moveId;
    p.recoveryFrames = recoveryFrames;
    this.send('combat:whiff');
  }

  knockdown(fighter, hard, faceUp, headTowardOpponent, x, y, z) {
    const p = this.begin('combat:knockdown');
    p.fighter = fighter;
    p.hard = hard;
    p.faceUp = faceUp;
    p.headTowardOpponent = headTowardOpponent;
    p.x = x;
    p.y = y;
    p.z = z;
    this.send('combat:knockdown');
  }

  wakeup(fighter, option, invulnTicks) {
    const p = this.begin('combat:wakeup');
    p.fighter = fighter;
    p.option = option;
    p.invulnTicks = invulnTicks;
    this.send('combat:wakeup');
  }

  comboEnd(attacker, defender, hits, damage, maxScaling, wallUsed) {
    const p = this.begin('combat:combo-end');
    p.attacker = attacker;
    p.defender = defender;
    p.hits = hits;
    p.damage = damage;
    p.maxScaling = maxScaling;
    p.wallUsed = wallUsed;
    this.send('combat:combo-end');
  }
}
